import { Box, TextField } from "@mui/material";
import React, { forwardRef } from "react";
import { useTheme } from "@mui/material/styles";
import InputAdornment from "@mui/material/InputAdornment";
import FaIcon from "@/components/utilities/fa-icon";
import Label from "@/components/form/label";
import InputErrorMessage from "@/components/utilities/input-error-message";
import { InputOnWhiteProps } from "@/types/form";

const RHFTextField = forwardRef<HTMLInputElement, InputOnWhiteProps>(
    (
        {
            sx,
            error,
            errorMessage = "",
            label,
            startAdornment,
            endAdornment,
            closeIcon,
            searchIcon,
            onClickAdornment,
            variant = "outlined",
            borderless = false,
            noPadding = false,
            normalizeDigits = false,
            containerSx,
            InputProps,
            onChange,
            ...props
        },
        ref
    ) => {
        const theme = useTheme();
        const toEnglishDigits = (value: string) => value.replace(/[۰-۹]/g, d => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString()).replace(/[٠-٩]/g, d => "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString());

        const handleClear = () => {
            if (onClickAdornment) {
                onClickAdornment();
                return;
            }

            onChange?.({
                target: { value: "" },
            } as React.ChangeEvent<HTMLInputElement>);
        };

        const renderEndAdornment = () => {
            if (endAdornment !== undefined) return endAdornment;
            if (!closeIcon && !searchIcon) return InputProps?.endAdornment ?? null;

            return (
                <InputAdornment position="end">
                    {(searchIcon || closeIcon) &&
                    typeof props.value === "string" &&
                    props.value.length > 0 ? (
                        <FaIcon
                            onClick={handleClear}
                            fontSize={18}
                            faIcon="fa-times"
                            sx={{ cursor: "pointer", pt:.1 }}
                            color={theme.palette.grey[500]}
                        />
                    ) : searchIcon ? (
                        <FaIcon
                            faIcon="fa-search"
                            fontSize={20}
                            sx={{ m: 0 }}
                            color={theme.palette.grey[600]}
                        />
                    ) : null}
                </InputAdornment>
            );
        };

        const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
            if (!normalizeDigits || typeof e.target.value !== "string") {
                onChange?.(e);
                return;
            }

            const rawValue = e.target.value;
            const normalizedValue = toEnglishDigits(rawValue);

            if (normalizedValue === rawValue) {
                onChange?.(e);
                return;
            }

            onChange?.({
                ...e,
                target: { ...e.target, value: normalizedValue },
            } as React.ChangeEvent<HTMLInputElement>);
        };

        return (
            <Box width= "100%" height="100%" sx={containerSx}>

                {label && <Label>{ label }</Label>}

                <TextField
                    inputRef = {ref}
                    onChange = {handleChange}
                    variant = {variant}
                    InputProps = {{
                        ...InputProps,
                        startAdornment:
                            startAdornment !== undefined
                                ? startAdornment
                                : InputProps?.startAdornment,
                        endAdornment: renderEndAdornment(),
                    }}
                    sx = {{
                        mb: 0,
                        ...sx,
                        ...(noPadding && {
                            "& .MuiInputBase-input": {
                                padding: 0.5,
                                paddingTop: .8,
                            },
                        }),
                        ...(borderless && {
                            "& .MuiOutlinedInput-root": {
                                boxShadow: "none",
                                "& fieldset": {
                                    border: "none !important",
                                },
                                "&:hover fieldset": {
                                    border: "none !important",
                                },
                                "&.Mui-focused fieldset": {
                                    border: "none !important",
                                },
                            },
                        }),
                    }}
                    error = {error}
                    {...props}
                />

                <InputErrorMessage error = {error} errorMessage = {errorMessage} />
            </Box>
        );
    }
);

RHFTextField.displayName = "RHFTextField";
export default RHFTextField;
